import { useState } from "react";
import { Card, MonoMeta, StateWord } from "@/design/primitives";
import { WORK_STATE, isDoneGroup } from "@/data/workBoard";
import { StageTrack, PhaseBar } from "@/screens/shared/PhaseTrack";

// ── The goal axis: a chunk (or dated group, or the needs-a-goal dock) with the
// work filed under it. Lifted out of the Work page (4→2 council, 2026-10-22)
// so the In Flight page files the same projects under the same goals instead
// of forking a second list. The Work page renders these unchanged — same
// words, same test ids.
//
// Two shapes of group exist by construction (P2): CHUNKS (payload carries the
// goals-module card verbatim — the chunk IS work in motion, phases live in the
// record) and TASK GROUPS (dated goal groups + the needs-a-goal dock — pure
// containers of task rows). A chunk shows its own lines and counts; a task
// group shows its filed rows. Header opens the detail overlay; the task list
// folds behind a count toggle so 43 rows never drown the page.

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 86400) return "today";
  const d = Math.floor(s / 86400);
  return d === 1 ? "yesterday" : `${d} days ago`;
};

// ── Task row: one line, Airmail density (WorkingRow grammar) ──
export const WorkTaskRow = ({ row, onOpen }) => {
  const p = row.payload || {};
  const st = WORK_STATE[row.state] || WORK_STATE.UNSORTED;
  const taken = p.jeff?.kitTaken;
  return (
    <Card
      role="button"
      tabIndex={0}
      data-testid={`work-row-${row.slug}`}
      data-state={row.state}
      aria-label={`${p.title || row.slug}, ${p.brand || ""}. Open details`}
      onClick={() => onOpen?.(row)}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") &&
        (e.preventDefault(), onOpen?.(row))
      }
      className="flex cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 text-left hover:border-os-border-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink"
    >
      <span className="min-w-0 flex-[1] basis-48 truncate font-semibold tracking-[-0.01em] text-os-ink">
        {p.title || row.slug}
      </span>
      <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">
        {p.brand === "Portfolio system" ? "" : p.brand}
      </span>
      {taken ? (
        <span
          data-testid={`work-taken-${row.slug}`}
          className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-green"
        >
          Kit is on it
        </span>
      ) : (
        <StateWord tone={st.tone}>{st.word}</StateWord>
      )}
      {(p.next || p.blockersLine) && (
        <span
          data-testid={`work-next-${row.slug}`}
          className="min-w-0 flex-[2] basis-64 truncate text-[0.8125rem] text-os-ink-3"
        >
          {taken?.note || p.next || p.blockersLine}
        </span>
      )}
      {p.asOf && (
        <span className="ml-auto font-mono text-[0.625rem] text-os-ink-4">
          {timeAgo(p.asOf)}
        </span>
      )}
    </Card>
  );
};

export const WorkGroup = ({ group, tasks, onOpen, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const p = group.payload || {};
  const st = WORK_STATE[group.state] || WORK_STATE.UNSORTED;
  void isDoneGroup;
  const isChunk = Boolean(p.rawState || p.phaseCount !== undefined); // goals-module card shape
  return (
    <section
      data-testid={`work-group-${group.slug}`}
      className="flex flex-col gap-2"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <button
          type="button"
          data-testid={`work-group-open-${group.slug}`}
          onClick={() => onOpen?.(group)}
          className="min-w-0 max-w-full truncate text-left text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink hover:underline underline-offset-2"
        >
          {p.title || group.slug}
        </button>
        <StateWord tone={st.tone}>{st.word}</StateWord>
        {tasks.length > 0 && (
          <button
            type="button"
            data-testid={`work-group-toggle-${group.slug}`}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="ml-auto shrink-0 font-mono text-[0.625rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
          >
            {open ? "hide" : ""}
            {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
          </button>
        )}
      </div>
      {(p.goal || p.why) && (
        <p className="text-[0.8125rem] leading-[1.4] text-os-ink-3">
          {p.goal || p.why}
        </p>
      )}
      {/* Jeff 10/26: the stage + the phases, drawn — INTAKE ▸ PLAN ▸ EXECUTE ▸
          TEST ▸ CLOSE with the current step lit, plus the real planned-phase
          ticks and the checks counter. Prose alone buried this. */}
      {isChunk ? (
        <div
          data-testid={`work-group-track-${group.slug}`}
          className="flex flex-wrap items-center gap-x-3 gap-y-1.5"
        >
          <StageTrack state={group.state} className="min-w-[10rem] flex-[3] basis-40" />
          <PhaseBar payload={p} />
          {p.brand && p.brand !== "Across the portfolio" ? (
            <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
              {p.brand}
            </span>
          ) : null}
        </div>
      ) : null}
      {isChunk ? (
        <div className="flex flex-col gap-1">
          <p
            data-testid={`work-group-next-${group.slug}`}
            className="text-[0.875rem] font-medium leading-[1.45] text-os-ink"
          >
            {p.next || "Kit is working the plan on this goal"}
          </p>
          <p
            data-testid={`work-group-blockers-${group.slug}`}
            className="text-[0.8125rem] leading-[1.4] text-os-ink-3"
          >
            {p.blockersLine || ""}
          </p>
          <MonoMeta as="p">
            {(p.checksTotal || 0) > 0 &&
              `${p.checksVerified || 0} of ${p.checksTotal} checks verified`}
            {(p.checksTotal || 0) > 0 && (p.closeLeft || 0) > 0 ? " · " : ""}
            {(p.closeLeft || 0) > 0
              ? `close-out: ${p.closeLeft} items left`
              : null}
          </MonoMeta>
        </div>
      ) : null}
      {open && tasks.length > 0 && (
        <div className="flex flex-col gap-2">
          {tasks.map((t) => (
            <WorkTaskRow key={t.slug} row={t} onOpen={onOpen} />
          ))}
        </div>
      )}
      {open && tasks.length === 0 && !isChunk && (
        <Card quiet className="px-4 py-3">
          <p className="text-[0.8125rem] text-os-ink-3">
            Nothing filed under this goal yet.
          </p>
        </Card>
      )}
    </section>
  );
};

export default WorkGroup;
